import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Eu, Falha } from '../../acesso';
import { podeFazer } from '../../acesso';
import type { Sessao } from '../../auth';
import {
  ROTULO_DA_CAUSA,
  ROTULO_DO_AJUSTE,
  ROTULO_DO_RESULTADO,
  ROTULO_SEM_VERIFICACAO,
  anotar,
  concluir,
  decidirAjuste,
  lerCaso,
  lerEtapas,
  lerHosAgora,
  lerInvestigacao,
  liberarParaConferir,
  numeroDoCaso,
  pedirNovaConferencia,
  reabrir,
  registrarAjusteNoHos,
} from '../../auditoria';
import type {
  CasoCompleto,
  CausaDaDivergencia,
  ConfiancaDaConciliacao,
  ErroDoBalconista,
  EtapasDoCaso,
  Investigacao,
  ParecerDaEvidencia,
  LocalDaConferencia,
  MovimentoDoExtrato,
  ResultadoDaAuditoria,
} from '../../auditoria';
import { ErroDeArea, SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import type { Escrita } from '../../giro';
import { FotoDoProduto } from '../auditoria/FotoDoProduto';
import {
  Laboratorio,
  Selo,
  comSinal,
  lerLista,
  novaChave,
  quandoBR,
  seloDe,
  ultimaCentral,
  unidades,
} from './comum';
import { TresNumeros } from './TresNumeros';
import './divergencias.css';

/**
 * INVESTIGAÇÃO E DECISÃO (dono, 13/10/2026) — a tela de quem decide, em quatro áreas:
 *
 *   1 Resumo do problema   2 Reconferência física   3 Movimentações do produto (razão do HOS)
 *   4 Resolver (resultado da investigação + causa + "foi erro do balconista?" + justificativa)
 *
 * O histórico completo fica fechado até alguém pedir. Toda escrita confirma antes, guarda quem e
 * quando no servidor, e tem os três desfechos (confirmada, recusada, incerta) — incerto nunca
 * aparece como recusa.
 */

const CONFIANCA: Readonly<
  Record<ConfiancaDaConciliacao, { rotulo: string; tom: string; texto: string }>
> = {
  CONFIAVEL: {
    rotulo: 'Confiável',
    tom: 'ok',
    texto: 'Contagens e saldo do HOS do mesmo instante, sem movimento entre elas.',
  },
  APROXIMADA: {
    rotulo: 'Aproximada',
    tom: 'atencao',
    texto: 'A conta fecha, mas com ressalvas (veja abaixo).',
  },
  INCONCLUSIVA: {
    rotulo: 'Inconclusiva',
    tom: 'critico',
    texto: 'Faltam dados para afirmar uma diferença.',
  },
  AGUARDANDO: {
    rotulo: 'Aguardando contagens',
    tom: 'neutro',
    texto: 'A comparação aparece quando prateleira e estoque forem contados.',
  },
};
type FiltroMov = 'tudo' | 'vendas' | 'entradas' | 'ajustes' | 'outros';
const FILTRO_MOV: readonly { v: FiltroMov; r: string }[] = [
  { v: 'tudo', r: 'Tudo' },
  { v: 'vendas', r: 'Vendas' },
  { v: 'entradas', r: 'Entradas' },
  { v: 'ajustes', r: 'Ajustes' },
  { v: 'outros', r: 'Outros' },
];
const noFiltro = (m: MovimentoDoExtrato, f: FiltroMov) =>
  f === 'tudo' ||
  (f === 'vendas' && (m.tipo === 'VENDA' || m.tipo === 'DEVOLUCAO' || m.tipo === 'CANCELAMENTO')) ||
  (f === 'entradas' && m.tipo === 'ENTRADA_NF') ||
  (f === 'ajustes' && m.tipo === 'AJUSTE') ||
  (f === 'outros' &&
    (m.tipo === 'OUTRO' || m.tipo === 'REPOSICAO_INTERNA' || m.tipo === 'NOTA_EM_CONFERENCIA'));

const resultadoPrevisto = (c: CausaDaDivergencia, e: ErroDoBalconista): ResultadoDaAuditoria =>
  e === 'SIM'
    ? 'ERRO_BALCONISTA_CONFIRMADO'
    : e === 'INCERTO'
      ? 'INCONCLUSIVO'
      : c === 'PROBLEMA_INTEGRACAO'
        ? 'PROBLEMA_DADOS'
        : 'BALCONISTA_CORRETO';

type Acao = null | 'resolver' | 'nova' | 'investigar' | 'ajuste' | 'reabrir';
type Aviso =
  | { tipo: 'ok'; texto: string }
  | { tipo: 'erro'; texto: string }
  | { tipo: 'incerta'; texto: string };

export function AnaliseDoCaso({ sessao, eu, id }: { sessao: Sessao; eu: Eu | null; id: string }) {
  const decide = podeFazer(eu, 'divergencia.decidir');
  const reabre = podeFazer(eu, 'auditoria.reabrir');
  const [caso, setCaso] = useState<CasoCompleto | null>(null);
  const [inv, setInv] = useState<Investigacao | null>(null);
  const [etapas, setEtapas] = useState<EtapasDoCaso | null>(null);
  const [falha, setFalha] = useState<Falha | null>(null);
  const [acao, setAcao] = useState<Acao>(null);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [historico, setHistorico] = useState(false);
  const [filtroMov, setFiltroMov] = useState<FiltroMov>('tudo');
  const chave = useRef(novaChave());
  const volta = useMemo(() => {
    const i = window.location.hash.indexOf('?');
    const v = i < 0 ? null : new URLSearchParams(window.location.hash.slice(i + 1)).get('volta');
    return v?.startsWith('#/') ? v : ultimaCentral();
  }, []);

  const carregar = useCallback(async () => {
    const [c, i, e] = await Promise.all([lerCaso(id), lerInvestigacao(id), lerEtapas(id)]);
    if (!c.ok) {
      setFalha(c.falha);
      return;
    }
    setFalha(null);
    setCaso(c.dados);
    if (i.ok) setInv(i.dados);
    if (e.ok) setEtapas(e.dados);
  }, [id]);
  useEffect(() => {
    void carregar();
  }, [carregar]);
  // As contagens chegam em tempo real enquanto o caso está aberto (Igor e Felipe contando agora).
  useEffect(() => {
    if (!caso || caso.estado === 'CONCLUIDA' || acao) return;
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') void carregar();
    }, 15_000);
    return () => window.clearInterval(t);
  }, [caso, acao, carregar]);

  const lista = lerLista();
  const pos = lista.indexOf(id);
  const proximo = pos >= 0 ? (lista[pos + 1] ?? null) : null;
  const linkDe = (x: string) => `#/caso?id=${x}&volta=${encodeURIComponent(volta)}`;

  /** Toda escrita termina aqui: confirmada recarrega; incerta pede nova tentativa (mesma chave). */
  const fim = async (r: Escrita<unknown>, ok: string) => {
    setEnviando(false);
    if (r.tipo === 'confirmada') {
      chave.current = novaChave();
      setAcao(null);
      setAviso({ tipo: 'ok', texto: ok });
      await carregar();
    } else if (r.tipo === 'incerta')
      setAviso({
        tipo: 'incerta',
        texto: 'Não deu para confirmar (conexão). Tente de novo — não duplica.',
      });
    else {
      setAviso({ tipo: 'erro', texto: r.mensagem });
      if (r.status === 409) await carregar();
    }
  };

  if (falha?.tipo === 'sem-acesso') return <SemAcesso area="A investigação da divergência" />;
  if (falha)
    return (
      <section className="dp">
        <ErroDeArea
          oQue={falha.tipo === 'offline' ? 'o caso (sem conexão)' : 'o caso'}
          aoTentar={() => void carregar()}
        />
      </section>
    );
  if (!caso)
    return (
      <section className="dp" aria-busy="true">
        <p className="dp-dica">Carregando o caso…</p>
      </section>
    );

  const aberto = caso.estado !== 'CONCLUIDA';
  const s = seloDe(caso);
  const conc = inv?.conciliacao ?? null;
  const ajustes = etapas?.ajustes ?? [];
  const pendente = ajustes.find((a) => a.estado === 'PENDENTE_NO_HOS') ?? null;
  const confirmado = ajustes.some((a) => a.estado === 'CONFIRMADO');
  const nv = inv?.contagens.naoVerificados ?? [];
  const nvDe = (l: LocalDaConferencia) => nv.find((x) => x.local === l) ?? null;
  const fisicoExato = conc?.fisico && conc.fisico.min === conc.fisico.max ? conc.fisico.min : null;
  const ultimaConclusao = [...caso.auditorias].reverse().find((a) => !a.reabertura) ?? null;

  return (
    <section className="dp">
      <nav className="dp-nav-caso" aria-label="Navegação do caso">
        <a className="dp-link" href={volta}>
          <Icone nome="arrow-l" tamanho={16} />
          Voltar às divergências
        </a>
        <span className="dp-acoes">
          <a
            className="dp-btn dp-btn-texto dp-btn-p"
            href={`#/caso?id=${id}&classica=1&volta=${encodeURIComponent(volta)}`}
          >
            Tela clássica
          </a>
          {proximo && (
            <a className="dp-btn dp-btn-p" href={linkDe(proximo)}>
              Próximo caso
              <Icone nome="chev-r" tamanho={14} />
            </a>
          )}
        </span>
      </nav>

      {aviso && (
        <div
          className={`dp-alerta dp-alerta-${aviso.tipo === 'ok' ? 'info' : aviso.tipo === 'erro' ? 'erro' : 'aviso'}`}
          role={aviso.tipo === 'ok' ? 'status' : 'alert'}
        >
          <Icone nome={aviso.tipo === 'ok' ? 'check' : 'warn'} tamanho={16} />
          <span style={{ flex: 1 }}>{aviso.texto}</span>
          <button
            type="button"
            className="dp-btn dp-btn-texto dp-btn-p"
            onClick={() => setAviso(null)}
            aria-label="Fechar aviso"
          >
            <Icone nome="x" tamanho={14} />
          </button>
        </div>
      )}

      {/* ÁREA 1 — RESUMO DO PROBLEMA */}
      <section className="dp-area" aria-labelledby="dp-a1">
        <div className="dp-produto-cab" style={{ gridTemplateColumns: '72px minmax(0,1fr)' }}>
          <FotoDoProduto url={caso.fotoProduto} nome={caso.produto} tamanho={72} />
          <div>
            <div className="dp-card-meta">
              {numeroDoCaso(caso.numero)} · {caso.setor} · B{caso.bloco}
              {caso.rodada > 1 ? ` · ${String(caso.rodada)}ª rodada` : ''}
            </div>
            <h1 className="dp-produto-nome" style={{ marginTop: 2 }}>
              {caso.produto}
            </h1>
            <Laboratorio nome={caso.fabricante} />
            <div className="dp-ids">
              {caso.codigo && (
                <span>
                  Código HOS <b>{caso.codigo}</b>
                </span>
              )}
              {caso.ean && (
                <span>
                  EAN <b>{caso.ean}</b>
                </span>
              )}
              <Selo selo={s.selo} titulo={s.motivo} />
            </div>
          </div>
        </div>
        <h2 className="dp-area-titulo" id="dp-a1">
          <span className="dp-area-n">1</span>Resumo do problema
        </h2>
        {caso.numeros ? (
          <>
            <dl className="dp-dados">
              <div>
                <dt>Balconista</dt>
                <dd>{caso.autoria ?? '—'}</dd>
              </div>
              <div>
                <dt>Divergência</dt>
                <dd>{quandoBR(caso.grandezas.contadoEm ?? caso.abertoEm)}</dd>
              </div>
            </dl>
            <TresNumeros
              n={inv?.numeros ?? caso.numeros}
              conc={inv?.conciliacao ?? null}
              explicacao={inv?.explicacao ?? null}
            />
          </>
        ) : (
          <>
            {inv && <p className="dp-frase">{inv.frase}</p>}
            <dl className="dp-dados">
              <div>
                <dt>Balconista</dt>
                <dd>{caso.autoria ?? '—'}</dd>
              </div>
              <div>
                <dt>Divergência</dt>
                <dd>{quandoBR(caso.grandezas.contadoEm ?? caso.abertoEm)}</dd>
              </div>
              <div>
                <dt>Contagem inicial (prateleira)</dt>
                <dd>
                  {caso.grandezas.prateleira === null ? '—' : unidades(caso.grandezas.prateleira)}
                </dd>
              </div>
              <div>
                <dt>Reposição solicitada</dt>
                <dd>{unidades(caso.solicitado)}</dd>
              </div>
              <div>
                <dt>Encontrada na reposição</dt>
                <dd>{unidades(caso.encontrado)}</dd>
              </div>
              <div>
                <dt>Diferença registrada</dt>
                <dd>{comSinal(caso.diferenca)}</dd>
              </div>
              <div>
                <dt>Saldo HOS no momento</dt>
                <dd>
                  {caso.estoqueHosAbertura === null
                    ? 'sem leitura'
                    : unidades(caso.estoqueHosAbertura)}
                </dd>
              </div>
            </dl>
          </>
        )}
      </section>

      {/* ÁREA 2 — RECONFERÊNCIA FÍSICA */}
      <section className="dp-area" aria-labelledby="dp-a2">
        <div className="dp-area-cab">
          <h2 className="dp-area-titulo" id="dp-a2">
            <span className="dp-area-n">2</span>Reconferência física
          </h2>
          {caso.rodada > 1 && <span className="dp-card-meta">Rodada {caso.rodada}</span>}
        </div>
        <div className="dp-dois">
          {(['PRATELEIRA', 'ESTOQUE'] as const).map((l) => {
            const ct = l === 'PRATELEIRA' ? inv?.contagens.prateleira : inv?.contagens.estoque;
            const n = nvDe(l);
            return (
              <div className="dp-local" key={l}>
                <div className="dp-local-cab">
                  <span>{l === 'PRATELEIRA' ? 'Prateleira' : 'Estoque'}</span>
                  {ct ? (
                    <span className="dp-selo dp-selo-ok">
                      <Icone nome="check" tamanho={12} traco={2} />
                      Concluída
                    </span>
                  ) : l === 'ESTOQUE' && inv?.contagens.estoqueMaisDe ? (
                    <span className="dp-selo dp-selo-ok">
                      <Icone nome="check" tamanho={12} traco={2} />
                      Mais de 12
                    </span>
                  ) : l === 'ESTOQUE' && inv?.contagens.buscaNoEstoque ? (
                    caso.modoEstoque === 'BUSCA' ? (
                      <span className="dp-selo dp-selo-ok">
                        <Icone nome="check" tamanho={12} traco={2} />
                        Busca concluída
                      </span>
                    ) : (
                      <span className="dp-selo dp-selo-atencao">
                        <Icone nome="warn" tamanho={12} traco={2} />
                        Só busca pontual
                      </span>
                    )
                  ) : n ? (
                    <span className="dp-selo dp-selo-atencao">
                      <Icone nome="warn" tamanho={12} traco={2} />
                      Não verificada
                    </span>
                  ) : (
                    <span className="dp-selo dp-selo-neutro">
                      <Icone nome="clock" tamanho={12} traco={2} />
                      Pendente
                    </span>
                  )}
                </div>
                {ct ? (
                  <>
                    <div className="dp-local-qtd">
                      {ct.qtd} <small>{ct.qtd === 1 ? 'unidade' : 'unidades'}</small>
                    </div>
                    <div className="dp-local-quem">
                      {ct.por ?? '—'} · {quandoBR(ct.em)}
                    </div>
                  </>
                ) : n ? (
                  <>
                    <div className="dp-local-quem">
                      <strong>{ROTULO_SEM_VERIFICACAO[n.motivo]}</strong>
                      {n.observacao ? ` — ${n.observacao}` : ''}
                    </div>
                    <div className="dp-local-quem">
                      {n.por ?? '—'} · {quandoBR(n.em)}
                    </div>
                    {decide && aberto && (
                      <button
                        type="button"
                        className="dp-btn dp-btn-p"
                        disabled={enviando}
                        onClick={() => {
                          setEnviando(true);
                          void liberarParaConferir(sessao, id, {
                            chave: chave.current,
                            local: l,
                          }).then((r) =>
                            fim(
                              r,
                              `${l === 'PRATELEIRA' ? 'A prateleira' : 'O estoque'} voltou para a fila de conferência.`,
                            ),
                          );
                        }}
                      >
                        <Icone nome="retry" tamanho={14} />
                        Mandar conferir de novo
                      </button>
                    )}
                  </>
                ) : l === 'ESTOQUE' && inv?.contagens.estoqueMaisDe ? (
                  <>
                    <div className="dp-local-qtd">
                      +{inv.contagens.estoqueMaisDe.piso} <small>unidades</small>
                    </div>
                    <div className="dp-local-quem">
                      {inv.contagens.estoqueMaisDe.por ?? '—'} ·{' '}
                      {quandoBR(inv.contagens.estoqueMaisDe.em)} — "Tem mais de 12": o produto
                      estava lá (sem contagem exata).
                    </div>
                  </>
                ) : l === 'ESTOQUE' && inv?.contagens.buscaNoEstoque ? (
                  <>
                    <div className="dp-local-quem">
                      <strong>
                        Busca pontual: achou {inv.contagens.buscaNoEstoque.qtd} de{' '}
                        {inv.contagens.buscaNoEstoque.alvo}
                      </strong>
                    </div>
                    <div className="dp-local-quem">
                      {inv.contagens.buscaNoEstoque.por ?? '—'} ·{' '}
                      {quandoBR(inv.contagens.buscaNoEstoque.em)} —{' '}
                      {caso.modoEstoque === 'BUSCA'
                        ? 'HOS acima de 6: a busca do que faltou basta.'
                        : 'não é contagem do estoque inteiro; a contagem integral ainda falta.'}
                    </div>
                  </>
                ) : (
                  <div className="dp-local-quem">Esperando a contagem.</div>
                )}
              </div>
            );
          })}
        </div>
        {conc && conc.confianca !== 'AGUARDANDO' && conc.fisico && (
          <div className="dp-total">
            <span>Total físico conciliado</span>
            <strong>
              {fisicoExato !== null
                ? unidades(fisicoExato)
                : `entre ${String(conc.fisico.min)} e ${String(conc.fisico.max)} unidades`}
            </strong>
          </div>
        )}
      </section>

      {/* ÁREA 3 — MOVIMENTAÇÕES DO PRODUTO */}
      <section className="dp-area" aria-labelledby="dp-a3">
        <div className="dp-area-cab">
          <h2 className="dp-area-titulo" id="dp-a3">
            <span className="dp-area-n">3</span>Movimentações do produto
          </h2>
          <span className="dp-acoes" style={{ alignItems: 'center' }}>
            {inv && (
              <span className="dp-card-meta">
                {inv.extrato.fonte === 'HOS_RAZAO'
                  ? 'Fonte: razão do HOS'
                  : 'Fonte: vendas lidas pelo InovaPay'}
                {inv.extrato.saldoAgora
                  ? ` · HOS agora: ${String(inv.extrato.saldoAgora.saldo)} · atualizado ${quandoBR(inv.extrato.saldoAgora.em)}`
                  : ' · HOS agora: sem leitura'}
              </span>
            )}
            {/* O ÚNICO "Atualizar HOS" do caso (14/10): lê o saldo ao vivo e o guarda no caso. */}
            {aberto && (decide || podeFazer(eu, 'divergencia.conferir_estoque')) && (
              <button
                type="button"
                className="dp-btn dp-btn-p"
                disabled={enviando}
                onClick={() => {
                  setEnviando(true);
                  void lerHosAgora(sessao, id).then((r) =>
                    fim(r, 'Saldo do HOS atualizado e guardado no caso.'),
                  );
                }}
              >
                <Icone nome="cycle" tamanho={14} />
                Atualizar HOS
              </button>
            )}
          </span>
        </div>
        {!inv ? (
          <p className="dp-dica">Lendo as movimentações…</p>
        ) : (
          <>
            {inv.extrato.aviso && (
              <div className="dp-alerta dp-alerta-aviso">
                <Icone nome="warn" tamanho={16} />
                {inv.extrato.aviso}
              </div>
            )}
            <dl className="dp-resumo">
              <div>
                <dt>
                  Saldo inicial
                  {inv.extrato.saldoInicialFonte === 'ABERTURA' ? ' (lido na abertura)' : ''}
                </dt>
                <dd>{inv.extrato.saldoInicial ?? '—'}</dd>
              </div>
              <div>
                <dt>Entradas</dt>
                <dd className="dp-entra">+{inv.extrato.resumo.entradas}</dd>
              </div>
              <div>
                <dt>Saídas</dt>
                <dd>−{inv.extrato.resumo.saidas}</dd>
              </div>
              <div className="dp-forte">
                <dt>Saldo teórico</dt>
                <dd>{inv.extrato.resumo.saldoTeorico ?? '—'}</dd>
              </div>
            </dl>
            <p className="dp-explica">{inv.extrato.explicacao}</p>
            <div className="dp-chips" role="group" aria-label="Filtrar movimentações">
              {FILTRO_MOV.map((x) => (
                <button
                  key={x.v}
                  type="button"
                  className="dp-chip"
                  aria-pressed={filtroMov === x.v}
                  onClick={() => setFiltroMov(x.v)}
                >
                  {x.r}
                </button>
              ))}
            </div>
            {inv.extrato.movimentos.filter((m) => noFiltro(m, filtroMov)).length === 0 ? (
              <p className="dp-dica">
                Nenhuma movimentação {filtroMov === 'tudo' ? '' : 'deste tipo '}depois da contagem
                do balconista ({quandoBR(inv.extrato.desde)}).
              </p>
            ) : (
              <table className="dp-tabela">
                <thead>
                  <tr>
                    <th scope="col">Horário</th>
                    <th scope="col">Movimento</th>
                    <th scope="col">Documento</th>
                    <th scope="col" className="dp-num">
                      Quantidade
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr data-informativo="true">
                    <td data-col="hora">{quandoBR(inv.extrato.desde)}</td>
                    <td data-col="mov">Estoque na hora da contagem</td>
                    <td data-col="doc">
                      {inv.extrato.saldoInicialFonte === 'RAZAO'
                        ? 'calculado pelo razão'
                        : 'foto da abertura'}
                    </td>
                    <td data-col="qtd" className="dp-num">
                      {inv.extrato.saldoInicial ?? '—'}
                    </td>
                  </tr>
                  {inv.extrato.movimentos
                    .filter((m) => noFiltro(m, filtroMov))
                    .map((m) => (
                      <tr
                        key={m.chave}
                        data-cancelado={m.cancelado}
                        data-informativo={!m.conta && !m.cancelado}
                      >
                        <td data-col="hora">
                          {m.em
                            ? quandoBR(m.em)
                            : m.dia
                              ? `${m.dia.slice(8, 10)}/${m.dia.slice(5, 7)} (sem hora)`
                              : 'sem hora'}
                        </td>
                        <td data-col="mov">
                          {m.detalhe ?? m.tipo}
                          {!m.conta && !m.cancelado ? ' · não muda o saldo' : ''}
                        </td>
                        <td data-col="doc">{m.documento ?? '—'}</td>
                        <td
                          data-col="qtd"
                          className={`dp-num ${m.qtd > 0 ? 'dp-entra' : 'dp-sai'}`}
                        >
                          {m.qtd > 0 ? '+' : m.qtd < 0 ? '−' : ''}
                          {Math.abs(m.qtd)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
            {inv.extrato.notasEmConferencia > 0 && (
              <p className="dp-dica">
                {inv.extrato.notasEmConferencia} nota(s) deste produto em conferência no HOS — ainda
                não entraram no estoque.
              </p>
            )}
          </>
        )}
      </section>

      {/* ÁREA 4 — RESOLVER */}
      <section className="dp-area" aria-labelledby="dp-a4">
        <h2 className="dp-area-titulo" id="dp-a4">
          <span className="dp-area-n">4</span>
          {aberto ? 'Resolver divergência' : 'Resolução'}
        </h2>
        {conc && (
          <div className="dp-local" style={{ background: 'var(--surface)' }}>
            <div className="dp-local-cab">
              <span>Resultado da investigação</span>
              <span className={`dp-selo dp-selo-${CONFIANCA[conc.confianca].tom}`}>
                {CONFIANCA[conc.confianca].rotulo}
              </span>
            </div>
            <p className="dp-explica" style={{ fontSize: 15, color: 'var(--ink-900)' }}>
              {conc.frase}
            </p>
            {conc.teorico !== null && conc.referencia && (
              <p className="dp-dica">
                Estoque teórico às {quandoBR(conc.referencia)}: {conc.teorico} (
                {conc.teoricoFonte === 'LEITURA_NA_HORA'
                  ? 'saldo do HOS lido na hora da contagem'
                  : 'reconstruído pelos movimentos'}
                ). {CONFIANCA[conc.confianca].texto}
              </p>
            )}
            {conc.ressalvas.length > 0 && (
              <ul className="dp-ressalvas">
                {conc.ressalvas.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Ajustes no HOS: o InovaPay não escreve no HOS — registra o que alguém precisa lançar. */}
        {ajustes.length > 0 && (
          <div className="dp-local">
            <div className="dp-local-cab">
              <span>Ajustes no HOS</span>
            </div>
            {ajustes.map((a) => (
              <div key={a.id} className="dp-card-linha" style={{ justifyContent: 'space-between' }}>
                <span>
                  <strong>{comSinal(a.ajuste)}</strong> (físico {a.fisicoTotal} × HOS {a.saldoHos})
                  · {ROTULO_DO_AJUSTE[a.estado]}
                  {a.referencia ? ` · ref. ${a.referencia}` : ''} · {a.pessoa ?? '—'}{' '}
                  {quandoBR(a.em)}
                </span>
                {a.estado === 'PENDENTE_NO_HOS' && decide && (
                  <ConfirmarAjuste
                    enviando={enviando}
                    aoDecidir={async (decisao, referencia) => {
                      setEnviando(true);
                      const r = await decidirAjuste(sessao, a.id, { decisao, referencia });
                      await fim(
                        r,
                        decisao === 'CONFIRMADO'
                          ? 'Ajuste confirmado no HOS.'
                          : 'Ajuste cancelado.',
                      );
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {!aberto ? (
          <>
            {ultimaConclusao && (
              <p className="dp-explica">
                Concluído por <strong>{ultimaConclusao.por ?? '—'}</strong> em{' '}
                {quandoBR(ultimaConclusao.em)}:{' '}
                <strong>{ROTULO_DO_RESULTADO[ultimaConclusao.resultado]}</strong>
                {ultimaConclusao.observacao ? ` — “${ultimaConclusao.observacao}”` : ''}
              </p>
            )}
            {reabre && caso.config.reaberturaPermitida && acao !== 'reabrir' && (
              <div className="dp-acoes">
                <button type="button" className="dp-btn" onClick={() => setAcao('reabrir')}>
                  <Icone nome="retry" tamanho={16} />
                  Reabrir caso
                </button>
              </div>
            )}
            {acao === 'reabrir' && (
              <FormTexto
                titulo="Por que reabrir? A conclusão anterior fica no histórico."
                botao="Reabrir caso"
                enviando={enviando}
                aoCancelar={() => setAcao(null)}
                aoEnviar={async (motivo) => {
                  setEnviando(true);
                  const r = await reabrir(sessao, id, { motivo, versao: caso.versao });
                  await fim(r, 'Caso reaberto: volta para a conferência (rodada nova).');
                }}
              />
            )}
          </>
        ) : !decide ? (
          <p className="dp-dica">
            Quem decide é o administrador. Você pode acompanhar as contagens aqui.
          </p>
        ) : (
          <>
            {!acao && (
              <div className="dp-acoes">
                <button
                  type="button"
                  className="dp-btn dp-btn-primario"
                  onClick={() => setAcao('resolver')}
                >
                  <Icone nome="check" tamanho={16} />
                  Resolver divergência
                </button>
                {caso.etapa !== 'PRATELEIRA' && (
                  <button type="button" className="dp-btn" onClick={() => setAcao('nova')}>
                    <Icone nome="retry" tamanho={16} />
                    Solicitar nova conferência
                  </button>
                )}
                <button type="button" className="dp-btn" onClick={() => setAcao('investigar')}>
                  <Icone nome="clock" tamanho={16} />
                  Manter em investigação
                </button>
                {caso.etapa === 'AJUSTE' && !pendente && fisicoExato !== null && (
                  <button type="button" className="dp-btn" onClick={() => setAcao('ajuste')}>
                    <Icone nome="edit" tamanho={16} />
                    Registrar ajuste no HOS
                  </button>
                )}
              </div>
            )}
            {acao === 'resolver' && (
              <FormResolver
                etapa={caso.etapa}
                pendente={pendente !== null}
                enviando={enviando}
                semReposicao={caso.deEstoque === true}
                pareceres={inv?.pareceres ?? null}
                aoCancelar={() => setAcao(null)}
                aoEnviar={async (causa, contagem, reposicao, observacao) => {
                  setEnviando(true);
                  const erro = erroDasDuas(contagem, reposicao);
                  const r = await concluir(sessao, id, {
                    resultado: resultadoPrevisto(causa, erro),
                    observacao,
                    versao: caso.versao,
                    causa,
                    erroContagem: contagem,
                    falhaReposicao: reposicao,
                    desfecho: confirmado
                      ? 'COM_AJUSTE'
                      : causa === 'PROBLEMA_INTEGRACAO'
                        ? 'PROBLEMA_DADOS'
                        : erro === 'INCERTO'
                          ? 'SEM_EVIDENCIA'
                          : 'SEM_AJUSTE',
                    ...(caso.etapa !== 'AJUSTE' ? { pularEtapas: true } : {}),
                  });
                  await fim(r, 'Divergência resolvida.');
                }}
              />
            )}
            {acao === 'nova' && (
              <FormNovaConferencia
                enviando={enviando}
                aoCancelar={() => setAcao(null)}
                aoEnviar={async (etapa, motivo) => {
                  setEnviando(true);
                  const r = await pedirNovaConferencia(sessao, id, {
                    versao: caso.versao,
                    etapa,
                    motivo,
                  });
                  await fim(
                    r,
                    'Nova conferência pedida: rodada nova, as contagens antigas ficam no histórico.',
                  );
                }}
              />
            )}
            {acao === 'investigar' && (
              <FormTexto
                titulo="O que falta investigar? Fica registrado no caso, que continua aberto."
                botao="Registrar"
                enviando={enviando}
                aoCancelar={() => setAcao(null)}
                aoEnviar={async (texto) => {
                  setEnviando(true);
                  const r = await anotar(sessao, id, {
                    texto: `Em investigação: ${texto}`,
                    versao: caso.versao,
                  });
                  await fim(r, 'Anotado. O caso segue em investigação.');
                }}
              />
            )}
            {acao === 'ajuste' && fisicoExato !== null && (
              <FormTexto
                titulo={`Físico conciliado: ${String(fisicoExato)}. O InovaPay calcula a diferença contra o saldo do HOS de agora e registra o ajuste como PENDENTE — alguém precisa lançar no HOS e confirmar aqui. Como a contagem total foi feita?`}
                botao="Registrar ajuste pendente"
                enviando={enviando}
                aoCancelar={() => setAcao(null)}
                aoEnviar={async (motivo) => {
                  setEnviando(true);
                  const r = await registrarAjusteNoHos(sessao, id, {
                    versao: caso.versao,
                    chave: chave.current,
                    fisicoTotal: fisicoExato,
                    motivo,
                  });
                  await fim(r, 'Ajuste registrado como pendente no HOS.');
                }}
              />
            )}
          </>
        )}
      </section>

      <section className="dp-area">
        <button
          type="button"
          className="dp-btn dp-btn-texto"
          style={{ alignSelf: 'flex-start' }}
          aria-expanded={historico}
          onClick={() => setHistorico((h) => !h)}
        >
          <Icone nome={historico ? 'chev-d' : 'chev-r'} tamanho={16} />
          {historico ? 'Esconder histórico' : 'Ver histórico completo'}
        </button>
        {historico && (
          <ol className="dp-historico">
            {[...caso.linhaDoTempo].reverse().map((e, i) => (
              <li key={`${e.em}-${String(i)}`}>
                <time dateTime={e.em}>{quandoBR(e.em)}</time>
                <span>
                  {e.texto}
                  {e.detalhe && <small>{e.detalhe}</small>}
                </span>
              </li>
            ))}
          </ol>
        )}
      </section>
    </section>
  );
}

/* ---------- formulários ---------- */

/** Mínimo da justificativa quando a evidência pede conciliação escrita (igual ao servidor). */
const JUSTIFICATIVA_MINIMA = 20;
const RESPOSTAS: readonly { v: ErroDoBalconista; r: string }[] = [
  { v: 'NAO', r: 'Não' },
  { v: 'SIM', r: 'Sim, confirmado' },
  { v: 'INCERTO', r: 'Não dá para saber' },
];
/** "Foi erro do balconista?" segue só a falha de reposição (dono, 14/10); igual ao servidor. */
const erroDasDuas = (
  _contagem: ErroDoBalconista,
  reposicao: ErroDoBalconista | null,
): ErroDoBalconista => reposicao ?? 'NAO';

function Pergunta({
  nome,
  titulo,
  dica,
  valor,
  parecer,
  aoMudar,
}: {
  nome: string;
  titulo: string;
  dica: string;
  valor: ErroDoBalconista | null;
  parecer: ParecerDaEvidencia | null;
  aoMudar: (v: ErroDoBalconista) => void;
}) {
  return (
    <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
      <legend className="dp-rotulo" style={{ marginBottom: 4 }}>
        {titulo}
      </legend>
      <p className="dp-dica" style={{ margin: '0 0 8px' }}>
        {dica}
      </p>
      <div className="dp-motivos">
        {RESPOSTAS.map((o) => {
          const bloqueado = o.v === 'SIM' && parecer !== null && !parecer.permitido;
          return (
            <label
              key={o.v}
              className="dp-opcao"
              style={bloqueado ? { opacity: 0.5 } : undefined}
              title={bloqueado ? (parecer?.motivo ?? undefined) : undefined}
            >
              <input
                type="radio"
                name={nome}
                checked={valor === o.v}
                disabled={bloqueado}
                onChange={() => aoMudar(o.v)}
              />
              {o.r}
            </label>
          );
        })}
      </div>
      {parecer && (!parecer.permitido || (valor === 'SIM' && parecer.exigeJustificativa)) && (
        <p className="dp-dica" style={{ margin: '6px 0 0', color: 'var(--warn-txt)' }}>
          {parecer.motivo}
        </p>
      )}
    </fieldset>
  );
}

function FormResolver({
  etapa,
  pendente,
  enviando,
  semReposicao,
  pareceres,
  aoCancelar,
  aoEnviar,
}: {
  etapa: string;
  pendente: boolean;
  enviando: boolean;
  /** Prateleira acima do HOS: nenhuma reposição foi pedida, então não há falha de reposição. */
  semReposicao: boolean;
  pareceres: Investigacao['pareceres'] | null;
  aoCancelar: () => void;
  aoEnviar: (
    c: CausaDaDivergencia,
    contagem: ErroDoBalconista,
    reposicao: ErroDoBalconista | null,
    obs: string,
  ) => Promise<void>;
}) {
  const [causa, setCausa] = useState<CausaDaDivergencia | null>(null);
  const [contagem, setContagem] = useState<ErroDoBalconista | null>(null);
  // Dono (14/10): "o que importa é se o produto tinha quando ele falou que não achou". Se a
  // conferência do estoque achou, já vem marcado SIM — quem decide só confirma.
  const [reposicao, setReposicao] = useState<ErroDoBalconista | null>(
    semReposicao ? 'NAO' : pareceres?.falhaReposicao.permitido ? 'SIM' : null,
  );
  const [obs, setObs] = useState('');
  const [conferir, setConferir] = useState(false);
  const exige =
    (contagem === 'SIM' && pareceres?.erroContagem.exigeJustificativa === true) ||
    (reposicao === 'SIM' && pareceres?.falhaReposicao.exigeJustificativa === true);
  const minimo = exige ? JUSTIFICATIVA_MINIMA : 5;
  const pronto =
    causa !== null && contagem !== null && reposicao !== null && obs.trim().length >= minimo;
  const erro =
    contagem && reposicao ? erroDasDuas(contagem, semReposicao ? null : reposicao) : null;
  return (
    <form
      className="dp-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!pronto || !causa || !contagem || !reposicao) return;
        if (!conferir) {
          setConferir(true);
          return;
        }
        void aoEnviar(causa, contagem, semReposicao ? null : reposicao, obs.trim());
      }}
    >
      {pendente && (
        <div className="dp-alerta dp-alerta-aviso">
          <Icone nome="warn" tamanho={16} />
          Há um ajuste esperando no HOS: confirme ou cancele antes de resolver.
        </div>
      )}
      {etapa !== 'AJUSTE' && (
        <div className="dp-alerta dp-alerta-info">
          <Icone nome="clock" tamanho={16} />
          As contagens ainda não terminaram. Resolver agora fica registrado como "concluído antes do
          fim das contagens".
        </div>
      )}
      <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
        <legend className="dp-rotulo" style={{ marginBottom: 8 }}>
          Causa
        </legend>
        <div className="dp-motivos">
          {(Object.keys(ROTULO_DA_CAUSA) as CausaDaDivergencia[]).map((c) => (
            <label key={c} className="dp-opcao">
              <input
                type="radio"
                name="dp-causa"
                checked={causa === c}
                onChange={() => {
                  setCausa(c);
                  setConferir(false);
                }}
              />
              {ROTULO_DA_CAUSA[c]}
            </label>
          ))}
        </div>
      </fieldset>
      {!semReposicao && (
        <Pergunta
          nome="dp-falha-reposicao"
          titulo="1 · Falha de reposição confirmada? (conta para o bloqueio de 30%)"
          dica="O balconista disse que não achou para repor, e a conferência do estoque achou o produto lá."
          valor={reposicao}
          parecer={pareceres?.falhaReposicao ?? null}
          aoMudar={(v) => {
            setReposicao(v);
            setConferir(false);
          }}
        />
      )}
      <Pergunta
        nome="dp-erro-contagem"
        titulo={`${semReposicao ? '1' : '2'} · Erro de contagem da prateleira? (só acompanhamento)`}
        dica="A prateleira declarada é diferente da reconferência. Não bloqueia: a prateleira vende entre uma contagem e outra."
        valor={contagem}
        parecer={pareceres?.erroContagem ?? null}
        aoMudar={(v) => {
          setContagem(v);
          setConferir(false);
        }}
      />
      <label className="dp-rotulo">
        Justificativa (obrigatória
        {exige ? `, mínimo ${String(JUSTIFICATIVA_MINIMA)} caracteres` : ''})
        <textarea
          className="dp-campo"
          value={obs}
          maxLength={1000}
          onChange={(e) => {
            setObs(e.target.value);
            setConferir(false);
          }}
          placeholder={
            exige
              ? 'Como você conciliou o que mudou entre a contagem e a reconferência?'
              : 'O que as contagens e as movimentações mostraram?'
          }
        />
      </label>
      {conferir && causa && erro && (
        <div className="dp-alerta dp-alerta-aviso" role="alert">
          <Icone nome="warn" tamanho={16} />
          <span>
            Confirma? Causa: <strong>{ROTULO_DA_CAUSA[causa]}</strong> · Erro de contagem:{' '}
            <strong>{RESPOSTAS.find((o) => o.v === contagem)?.r}</strong>
            {!semReposicao && (
              <>
                {' '}
                · Falha de reposição: <strong>{RESPOSTAS.find((o) => o.v === reposicao)?.r}</strong>
              </>
            )}{' '}
            · Resultado: <strong>{ROTULO_DO_RESULTADO[resultadoPrevisto(causa, erro)]}</strong>.
            Fica no histórico e só volta atrás reabrindo o caso.
          </span>
        </div>
      )}
      <div className="dp-form-botoes">
        <button type="button" className="dp-btn dp-btn-texto" onClick={aoCancelar}>
          Cancelar
        </button>
        <button
          type="submit"
          className="dp-btn dp-btn-primario"
          disabled={!pronto || enviando || pendente}
        >
          {enviando ? 'Salvando…' : conferir ? 'Confirmar resolução' : 'Resolver'}
        </button>
      </div>
    </form>
  );
}

function FormNovaConferencia({
  enviando,
  aoCancelar,
  aoEnviar,
}: {
  enviando: boolean;
  aoCancelar: () => void;
  aoEnviar: (etapa: 'PRATELEIRA' | 'ESTOQUE', motivo: string) => Promise<void>;
}) {
  const [etapa, setEtapa] = useState<'PRATELEIRA' | 'ESTOQUE'>('PRATELEIRA');
  const [motivo, setMotivo] = useState('');
  return (
    <form
      className="dp-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (motivo.trim().length >= 5) void aoEnviar(etapa, motivo.trim());
      }}
    >
      <p className="dp-dica">
        Abre uma rodada nova: as duas contagens são feitas de novo. As contagens antigas ficam no
        histórico.
      </p>
      <div className="dp-chips" role="group" aria-label="Começar por">
        {(['PRATELEIRA', 'ESTOQUE'] as const).map((l) => (
          <button
            key={l}
            type="button"
            className="dp-chip"
            aria-pressed={etapa === l}
            onClick={() => setEtapa(l)}
          >
            Começar pela {l === 'PRATELEIRA' ? 'prateleira' : 'contagem do estoque'}
          </button>
        ))}
      </div>
      <label className="dp-rotulo">
        Motivo
        <textarea
          className="dp-campo"
          value={motivo}
          maxLength={500}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Por que conferir de novo?"
        />
      </label>
      <div className="dp-form-botoes">
        <button type="button" className="dp-btn dp-btn-texto" onClick={aoCancelar}>
          Cancelar
        </button>
        <button
          type="submit"
          className="dp-btn dp-btn-escuro"
          disabled={motivo.trim().length < 5 || enviando}
        >
          {enviando ? 'Enviando…' : 'Pedir nova conferência'}
        </button>
      </div>
    </form>
  );
}

function FormTexto({
  titulo,
  botao,
  enviando,
  aoCancelar,
  aoEnviar,
}: {
  titulo: string;
  botao: string;
  enviando: boolean;
  aoCancelar: () => void;
  aoEnviar: (texto: string) => Promise<void>;
}) {
  const [texto, setTexto] = useState('');
  return (
    <form
      className="dp-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (texto.trim().length >= 5) void aoEnviar(texto.trim());
      }}
    >
      <label className="dp-rotulo">
        {titulo}
        <textarea
          className="dp-campo"
          value={texto}
          maxLength={1000}
          onChange={(e) => setTexto(e.target.value)}
        />
      </label>
      <div className="dp-form-botoes">
        <button type="button" className="dp-btn dp-btn-texto" onClick={aoCancelar}>
          Cancelar
        </button>
        <button
          type="submit"
          className="dp-btn dp-btn-escuro"
          disabled={texto.trim().length < 5 || enviando}
        >
          {enviando ? 'Enviando…' : botao}
        </button>
      </div>
    </form>
  );
}

function ConfirmarAjuste({
  enviando,
  aoDecidir,
}: {
  enviando: boolean;
  aoDecidir: (d: 'CONFIRMADO' | 'CANCELADO', referencia: string | null) => Promise<void>;
}) {
  const [aberto, setAberto] = useState(false);
  const [ref, setRef] = useState('');
  if (!aberto)
    return (
      <span className="dp-acoes">
        <button type="button" className="dp-btn dp-btn-p" onClick={() => setAberto(true)}>
          Lancei no HOS
        </button>
        <button
          type="button"
          className="dp-btn dp-btn-texto dp-btn-p"
          disabled={enviando}
          onClick={() => void aoDecidir('CANCELADO', null)}
        >
          Cancelar ajuste
        </button>
      </span>
    );
  return (
    <span className="dp-acoes">
      <input
        className="dp-campo"
        style={{ width: 180 }}
        value={ref}
        onChange={(e) => setRef(e.target.value)}
        placeholder="Nº do lançamento no HOS"
        aria-label="Referência do lançamento no HOS"
      />
      <button
        type="button"
        className="dp-btn dp-btn-escuro dp-btn-p"
        disabled={ref.trim().length < 3 || enviando}
        onClick={() => void aoDecidir('CONFIRMADO', ref.trim())}
      >
        Confirmar
      </button>
    </span>
  );
}
